// DeleteWalletEntryModal — managers delete a top-up or a manual adjustment
// that was added by mistake (owner, 2026-09-18). The dialog first asks the
// server what WOULD happen (wallet change, the Zoho payment that goes with
// it, or what blocks the deletion) and only then offers Delete, with a
// required reason. The server keeps a full snapshot in wallet_deletions.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDubai } from '../../../utils/dubaiTime';
import './walletDelete.css';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const METHOD = { bank_transfer: 'Bank Transfer', cash: 'Cash', online_payment: 'Online', card: 'Card' };

/** target: { kind: 'deposit' | 'transaction', id } */
export default function DeleteWalletEntryModal({ target, onClose, onDeleted }) {
    const [preview, setPreview] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const base = target.kind === 'deposit' ? `/api/wallet/deposits/${target.id}` : `/api/wallet/transactions/${target.id}`;

    useEffect(() => {
        let alive = true;
        axios.get(`${base}/delete-preview`)
            .then((res) => { if (alive) setPreview(res.data); })
            .catch((e) => { if (alive) setLoadError(e.response?.data?.error || 'Could not check this entry.'); });
        return () => { alive = false; };
    }, [base]);

    const entry = preview?.entry;
    const isTopUp = preview?.kind === 'deposit';
    const moved = !!preview && (Math.abs(preview.effects.cash_delta) > 0.009 || Math.abs(preview.effects.bonus_delta) > 0.009);
    const canDelete = !!preview?.deletable && reason.trim().length >= 3 && !busy;

    const submit = async () => {
        if (!canDelete) return;
        setBusy(true);
        setError('');
        try {
            const res = await axios.delete(base, { data: { reason: reason.trim() } });
            const r = res.data || {};
            const bits = [];
            if (Math.abs(r.cash_removed) > 0.009) bits.push(`${money(Math.abs(r.cash_removed))} cash ${r.cash_removed > 0 ? 'removed from' : 'returned to'} the wallet`);
            if (Math.abs(r.bonus_removed) > 0.009) bits.push(`${money(Math.abs(r.bonus_removed))} bonus ${r.bonus_removed > 0 ? 'removed' : 'returned'}`);
            if (r.zoho?.state === 'delete') bits.push(`Zoho payment #${r.zoho.payment_number} deleted`);
            toast.success(`Entry deleted${bits.length ? ` — ${bits.join(', ')}` : ''}.`);
            if (onDeleted) onDeleted(r);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not delete the entry.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="wdel__overlay" onClick={() => !busy && onClose()}>
            <div className="wdel__modal" role="dialog" aria-modal="true" aria-labelledby="wdel-title" onClick={(e) => e.stopPropagation()}>
                <h3 id="wdel-title" className="wdel__title">Delete this entry?</h3>

                {loadError ? (
                    <div className="wdel__error">{loadError}</div>
                ) : !preview ? (
                    <div className="wdel__loading">Checking the wallet and Zoho Books…</div>
                ) : (
                    <>
                        <div className="wdel__entry">
                            <div className="wdel__entryTop">
                                <span className="wdel__ref">{entry.reference}</span>
                                <span className="wdel__entryKind">
                                    {isTopUp ? `Wallet Top-up · ${METHOD[entry.payment_method] || entry.payment_method}` : 'Manual Adjustment'}
                                </span>
                                <span className="wdel__entryAmount">
                                    {entry.cash_amount !== 0 && money(entry.cash_amount)}
                                    {entry.bonus_amount !== 0 && `${entry.cash_amount !== 0 ? ' + ' : ''}${money(entry.bonus_amount)} bonus`}
                                </span>
                            </div>
                            <div className="wdel__entryMeta">
                                {formatDubai(entry.created_at, 'd MMM yy, HH:mm')}
                                {entry.by_name ? ` · ${entry.by_name}` : ''}
                                {entry.notes ? ` · ${entry.notes}` : ''}
                            </div>
                        </div>

                        {preview.deletable ? (
                            <>
                                <div className="wdel__sectionTitle">What will happen</div>
                                <ul className="wdel__effects">
                                    {moved ? (
                                        <>
                                            {Math.abs(preview.effects.cash_delta) > 0.009 && (
                                                <li>
                                                    Wallet cash <strong>{money(preview.wallet.cash)} → {money(preview.effects.cash_after)}</strong>
                                                </li>
                                            )}
                                            {Math.abs(preview.effects.bonus_delta) > 0.009 && (
                                                <li>
                                                    Wallet bonus <strong>{money(preview.wallet.bonus)} → {money(preview.effects.bonus_after)}</strong>
                                                </li>
                                            )}
                                        </>
                                    ) : (
                                        <li>The wallet balance does not change — this entry never credited it.</li>
                                    )}
                                    <li className={preview.zoho.state === 'delete' ? 'wdel__effect--zoho' : undefined}>
                                        {preview.zoho.note || 'Nothing changes in Zoho Books.'}
                                    </li>
                                    <li>The entry leaves the client’s history. A copy is kept under “Deleted”, with your name and reason.</li>
                                </ul>

                                <label className="wdel__label" htmlFor="wdel-reason">Reason</label>
                                <input
                                    id="wdel-reason"
                                    className="wdel__input"
                                    type="text"
                                    maxLength={500}
                                    placeholder="e.g. added twice by mistake"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    disabled={busy}
                                    autoFocus
                                />
                            </>
                        ) : (
                            <>
                                <div className="wdel__sectionTitle wdel__sectionTitle--bad">This entry cannot be deleted</div>
                                <ul className="wdel__blockers">
                                    {preview.blockers.map((b) => <li key={b}>{b}</li>)}
                                </ul>
                            </>
                        )}
                    </>
                )}

                {error && <div className="wdel__error">{error}</div>}

                <div className="wdel__actions">
                    <button type="button" className="wdel__btn wdel__btn--ghost" onClick={onClose} disabled={busy}>
                        {preview && !preview.deletable ? 'Close' : 'Cancel'}
                    </button>
                    {preview?.deletable && (
                        <button type="button" className="wdel__btn wdel__btn--danger" onClick={submit} disabled={!canDelete}>
                            {busy ? 'Deleting…' : 'Delete entry'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
